import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import PrintIcon from '@material-ui/icons/Print';
import {
  CarrierService,
  PackBox,
  PackLabel,
  PackOrder,
  PackSession,
} from '../../../services/fulfillment/types';
import { getShippoRates, shipOrder } from '../../../services/fulfillment/packApi';
import { MOCK_LABEL_PDF_URL } from '../../../services/fulfillment/packStubs';

interface Props {
  order: PackOrder;
  session: PackSession;
  onSessionChange: (s: PackSession) => void;
  onDone: () => void;
  onBack: () => void;
}

const FEDEX_SERVICES: CarrierService[] = ['FedEx Home Delivery', 'FedEx Ground'];
const USPS_SERVICES: CarrierService[] = ['USPS Priority Mail', 'USPS Ground Advantage'];

// ─── BoxLabelCard ─────────────────────────────────────────────────────────────

interface BoxLabelCardProps {
  box: PackBox;
  idx: number;
  label: PackLabel;
  calcWeight: number;
  hasAddressBlock: boolean;
  order: PackOrder;
  lineStates: PackSession['lineStates'];
  onUpdateLabel: (boxId: string, patch: Partial<PackLabel>) => void;
}

const BoxLabelCard: React.FC<BoxLabelCardProps> = ({
  box,
  idx,
  label,
  calcWeight,
  hasAddressBlock,
  order,
  lineStates,
  onUpdateLabel,
}) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [rates, setRates] = useState<Record<
    CarrierService,
    { cost: number; transitDays: string }
  > | null>(null);
  const [ratesLoading, setRatesLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const displayWeight = label.overrideWeightLbs ?? calcWeight;
  const services = label.carrier === 'FedEx' ? FEDEX_SERVICES : USPS_SERVICES;

  const fetchRates = useCallback(async (weightLbs: number) => {
    setRatesLoading(true);
    try {
      const r = await getShippoRates(weightLbs);
      setRates(r);
    } finally {
      setRatesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchRates(displayWeight), 500);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [displayWeight, fetchRates]);

  const itemsInBox = order.lines.filter(l => lineStates[l.shopifyLineItemGid]?.boxId === box.id);

  return (
    <Box
      sx={{
        mb: 2,
        border: '1px solid',
        borderColor: label.generated ? 'success.light' : 'grey.300',
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ px: 2, py: 1.5, bgcolor: label.generated ? '#f0faf4' : 'grey.50' }}>
        <Typography sx={{ fontWeight: 700 }}>
          Box {idx + 1} — {box.size}
        </Typography>
        <Typography sx={{ fontSize: 12, color: 'grey.600' }}>
          {itemsInBox.length} item(s)
        </Typography>
      </Box>

      <Box sx={{ px: 2, py: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Carrier</InputLabel>
            <Select
              label="Carrier"
              value={label.carrier}
              onChange={e => {
                const c = e.target.value as 'FedEx' | 'USPS';
                onUpdateLabel(box.id, {
                  carrier: c,
                  service: c === 'FedEx' ? 'FedEx Home Delivery' : 'USPS Priority Mail',
                });
              }}
              disabled={label.generated}
            >
              <MenuItem value="FedEx">FedEx</MenuItem>
              <MenuItem value="USPS">USPS</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>Service</InputLabel>
            <Select
              label="Service"
              value={label.service}
              onChange={e => onUpdateLabel(box.id, { service: e.target.value as CarrierService })}
              disabled={label.generated}
            >
              {services.map(s => (
                <MenuItem key={s} value={s}>
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 2 }}
                  >
                    <span>{s}</span>
                    {ratesLoading ? (
                      <CircularProgress size={12} />
                    ) : rates?.[s] ? (
                      <Typography
                        component="span"
                        sx={{ fontSize: 11, color: 'grey.500', whiteSpace: 'nowrap' }}
                      >
                        ${rates[s].cost.toFixed(2)} · {rates[s].transitDays}
                      </Typography>
                    ) : null}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            label="Weight (lbs)"
            type="number"
            value={label.overrideWeightLbs ?? calcWeight}
            onChange={e =>
              onUpdateLabel(box.id, {
                overrideWeightLbs: e.target.value ? +e.target.value : null,
              })
            }
            disabled={label.generated}
            sx={{ width: 140 }}
            helperText={
              label.carrier === 'USPS'
                ? `Calc: ${calcWeight} lbs — exact required`
                : `Calc: ${calcWeight} lbs`
            }
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {!label.generated ? (
            <Button
              variant="contained"
              disabled={hasAddressBlock}
              onClick={() => onUpdateLabel(box.id, { generated: true })}
            >
              Generate Label
            </Button>
          ) : (
            <>
              <Chip label="Label generated" color="success" size="small" />
              <Button
                size="small"
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={() => iframeRef.current?.contentWindow?.print()}
              >
                {label.printed ? 'Reprint' : 'Print Label'}
              </Button>
              {label.printed && <Chip label="Printed" size="small" variant="outlined" />}
            </>
          )}
        </Box>

        {label.generated && (
          <Box sx={{ mt: 0.5 }}>
            <iframe
              ref={iframeRef}
              src={`${MOCK_LABEL_PDF_URL}#toolbar=0`}
              width="100%"
              height={460}
              style={{ border: '1px solid #e0e0e0', borderRadius: 4, display: 'block' }}
              title={`Shipping label — Box ${idx + 1}`}
              onLoad={() => onUpdateLabel(box.id, { printed: label.printed })}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ─── MockPackLabelsStep ───────────────────────────────────────────────────────

const MockPackLabelsStep: React.FC<Props> = ({
  order,
  session,
  onSessionChange,
  onDone,
  onBack,
}) => {
  const [addressEditing, setAddressEditing] = useState(false);
  const [addressFields, setAddressFields] = useState(order.shippingAddress);
  const [addressRetrying, setAddressRetrying] = useState(false);
  const [shipping, setShipping] = useState(false);
  const [shipResult, setShipResult] = useState<{ success: boolean; error?: string } | null>(null);

  const hasAddressBlock = order.hasAddressError && !session.addressConfirmed;
  const allLabelsGenerated = session.boxes.every(b => session.labels[b.id]?.generated);

  const computeWeight = (boxId: string) => {
    const box = session.boxes.find(b => b.id === boxId);
    if (!box) return 0;
    const itemWeight = order.lines
      .filter(l => session.lineStates[l.shopifyLineItemGid]?.boxId === boxId)
      .reduce((sum, l) => sum + l.weightLbs, 0);
    return +(itemWeight + box.tareLbs).toFixed(2);
  };

  const updateLabel = (boxId: string, patch: Partial<PackLabel>) => {
    const existing = session.labels[boxId] ?? {
      boxId,
      carrier: 'FedEx',
      service: 'FedEx Home Delivery' as CarrierService,
      overrideWeightLbs: null,
      generated: false,
      printed: false,
    };
    onSessionChange({
      ...session,
      labels: { ...session.labels, [boxId]: { ...existing, ...patch } },
    });
  };

  const handleAddressRetry = async () => {
    setAddressRetrying(true);
    await new Promise(r => setTimeout(r, 900));
    setAddressRetrying(false);
    onSessionChange({ ...session, addressConfirmed: true });
  };

  const handleShip = async () => {
    setShipping(true);
    setShipResult(null);
    const result = await shipOrder(order.shopifyOrderId);
    setShipping(false);
    setShipResult(result);
  };

  if (shipResult?.success) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 6, pb: 4 }}>
        <CheckCircleOutlineIcon style={{ fontSize: 80, color: '#4caf50' }} />
        <Typography variant="h5" mt={2} mb={1}>
          Shipped!
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={4}>
          {order.shopifyOrderName} marked as shipped.
        </Typography>
        <Button variant="contained" onClick={onDone}>
          Back to Pack Queue
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Generate a label for each box, then ship.
        </Typography>
        <Button size="small" variant="text" onClick={onBack}>
          ← Packing Slips
        </Button>
      </Box>

      {/* Address section */}
      <Box
        sx={{
          mb: 3,
          p: 2,
          border: '1px solid',
          borderColor: hasAddressBlock ? 'error.light' : 'grey.200',
          borderRadius: 1,
          bgcolor: hasAddressBlock ? '#fff5f5' : 'white',
        }}
      >
        <Typography variant="subtitle2" mb={1}>
          Ship To
        </Typography>
        {hasAddressBlock && !addressEditing ? (
          <>
            <Alert severity="error" sx={{ mb: 1.5 }}>
              Address validation failed. Edit the address and retry.
            </Alert>
            <Typography sx={{ fontSize: 13, mb: 1 }}>
              {order.shippingAddress.address1}
              {order.shippingAddress.address2 && `, ${order.shippingAddress.address2}`}
              <br />
              {order.shippingAddress.city}, {order.shippingAddress.province}{' '}
              {order.shippingAddress.zip}
            </Typography>
            <Button size="small" variant="outlined" onClick={() => setAddressEditing(true)}>
              Edit Address
            </Button>
          </>
        ) : hasAddressBlock && addressEditing ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <TextField
              size="small"
              label="Address line 1"
              value={addressFields.address1}
              onChange={e => setAddressFields(f => ({ ...f, address1: e.target.value }))}
              fullWidth
            />
            <TextField
              size="small"
              label="Address line 2"
              value={addressFields.address2 ?? ''}
              onChange={e => setAddressFields(f => ({ ...f, address2: e.target.value || null }))}
              fullWidth
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                label="City"
                value={addressFields.city}
                onChange={e => setAddressFields(f => ({ ...f, city: e.target.value }))}
                sx={{ flex: 2 }}
              />
              <TextField
                size="small"
                label="State"
                value={addressFields.province}
                onChange={e => setAddressFields(f => ({ ...f, province: e.target.value }))}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                label="ZIP"
                value={addressFields.zip}
                onChange={e => setAddressFields(f => ({ ...f, zip: e.target.value }))}
                sx={{ flex: 1 }}
              />
            </Box>
            <Button
              variant="contained"
              disabled={addressRetrying}
              onClick={handleAddressRetry}
              startIcon={addressRetrying ? <CircularProgress size={14} /> : undefined}
            >
              {addressRetrying ? 'Verifying…' : 'Retry Validation'}
            </Button>
          </Box>
        ) : (
          <Typography sx={{ fontSize: 13, color: 'grey.700' }}>
            {addressFields.address1}
            {addressFields.address2 && `, ${addressFields.address2}`}
            <br />
            {addressFields.city}, {addressFields.province} {addressFields.zip}
            {session.addressConfirmed && (
              <Chip
                label="Verified"
                size="small"
                color="success"
                sx={{ ml: 1, verticalAlign: 'middle' }}
              />
            )}
          </Typography>
        )}
      </Box>

      {session.boxes.map((box, idx) => {
        const label: PackLabel = session.labels[box.id] ?? {
          boxId: box.id,
          carrier: 'FedEx',
          service: 'FedEx Home Delivery' as CarrierService,
          overrideWeightLbs: null,
          generated: false,
          printed: false,
        };
        return (
          <BoxLabelCard
            key={box.id}
            box={box}
            idx={idx}
            label={label}
            calcWeight={computeWeight(box.id)}
            hasAddressBlock={hasAddressBlock}
            order={order}
            lineStates={session.lineStates}
            onUpdateLabel={updateLabel}
          />
        );
      })}

      <Divider sx={{ my: 2 }} />

      {shipResult && !shipResult.success && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {shipResult.error} — Correct the address and try again.
        </Alert>
      )}

      <Button
        variant="contained"
        color="success"
        size="large"
        disabled={!allLabelsGenerated || shipping || hasAddressBlock}
        onClick={handleShip}
        startIcon={shipping ? <CircularProgress size={16} sx={{ color: 'white' }} /> : undefined}
      >
        {shipping ? 'Shipping…' : 'Ship It'}
      </Button>
      {!allLabelsGenerated && (
        <Typography sx={{ fontSize: 12, color: 'grey.500', mt: 0.75 }}>
          Generate all labels before shipping.
        </Typography>
      )}
    </Box>
  );
};

export default MockPackLabelsStep;
